<template>
  <div class="menu">
    <div class="link-container">
      <ul class="nav">
        <li class="nav__item" @click="sellSkin">
          <a class="desc">
            <b>
              {{ $t("Sell skin") }}
            </b>
          </a>
        </li>
        <li class="nav__item">
          <router-link
            class="nav-link"
            :to="{ name: 'ProductListPage', params: { itemType: 'all' } }"
          >
            <b>{{ $t("Market") }}</b>
          </router-link>
        </li>
      </ul>

      <!-- Currency Selector -->
      <div v-if="currencyOptions.length > 1" class="select-container">
        <Select2
          v-model="selectedCurrency"
          class="select2 single currency-select"
          :options="currencyOptions"
          @select="handleCurrencyChange"
        />
      </div>

      <!-- Language Selector -->
      <div
        v-if="languageOptions.length > 1"
        class="select-container language-select-container"
      >
        <Select2
          v-model="selectedLanguage"
          class="select2 single language-select"
          :options="languageOptions"
          @select="handleLanguageChange"
        />
      </div>
      <div class="user-menu" v-if="isAuth">
        <VDropdown
          :distance="21"
          placement="bottom"
          :triggers="['click']"
          :autoHide="true"
          ref="cartDropdown"
          @apply-show="openCartDropdown"
          @apply-hide="closeCartDropdown"
        >
          <button class="cart-button">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.6">
                <path
                  d="M8 22C8.55228 22 9 21.5523 9 21C9 20.4477 8.55228 20 8 20C7.44772 20 7 20.4477 7 21C7 21.5523 7.44772 22 8 22Z"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M19 22C19.5523 22 20 21.5523 20 21C20 20.4477 19.5523 20 19 20C18.4477 20 18 20.4477 18 21C18 21.5523 18.4477 22 19 22Z"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2.05078 2.05H4.05078L6.71078 14.47C6.80836 14.9249 7.06145 15.3315 7.42649 15.6199C7.79153 15.9082 8.24569 16.0603 8.71078 16.05H18.4908C18.946 16.0493 19.3873 15.8933 19.7418 15.6078C20.0964 15.3224 20.3429 14.9245 20.4408 14.48L22.0908 7.05H5.12078"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
            </svg>

            <div v-if="cartContents.length" class="cart-indicator">
              {{ cartContents.length }}
            </div>
          </button>

          <!-- Dropdown content -->
          <template #popper="{}">
            <CartDropdown
              :cartContents="cartContents"
              :userBalance="walletBalance"
              @close="handleCloseCartDropdown"
              @remove-item="removeItemFromCart"
              @openCartCheckout="openCartCheckout"
            />
          </template>
        </VDropdown>

        <router-link to="/profile/details" class="profile-button">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 0C6.383 0 4.25391 2.1291 4.25391 4.74609C4.25391 7.36309 6.383 9.49219 9 9.49219C11.617 9.49219 13.7461 7.36309 13.7461 4.74609C13.7461 2.1291 11.617 0 9 0Z"
              fill="#9D1616"
            />
            <path
              d="M14.9051 12.5928C13.6057 11.2734 11.8831 10.5469 10.0547 10.5469H7.94531C6.11691 10.5469 4.39432 11.2734 3.09495 12.5928C1.80193 13.9057 1.08984 15.6387 1.08984 17.4727C1.08984 17.7639 1.32595 18 1.61719 18H16.3828C16.674 18 16.9102 17.7639 16.9102 17.4727C16.9102 15.6387 16.1981 13.9057 14.9051 12.5928Z"
              fill="#9D1616"
            />
          </svg>
        </router-link>
      </div>
      <template v-else>
        <button class="button gray w-106" @click="openSignUp">
          {{ $t("Sign up") }}
        </button>
        <button class="button w-106" @click="openSignIn">
          {{ $t("Login") }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import Select2 from "vue3-select2-component";
import CartDropdown from "./CartDropdown.vue";

export default {
  name: "MobMenu",
  components: {
    Select2,
    CartDropdown,
  },
  props: {
    isAuth: {
      type: Boolean,
      required: true,
    },
    cartContents: {
      type: Array,
      required: true,
    },
  },
  mounted() {
    window.addEventListener("resize", () => {
      if (window.innerWidth >= 768) {
        this.$emit("closeMenu");
      }
    });
  },
  computed: {
    ...mapState("app", [
      "currencies",
      "languages",
      "currentLanguage",
      "currency",
    ]),

    currencyOptions() {
      if (!this.currencies) return [];
      return this.currencies.map(({ code, symbol }) => ({
        id: code,
        text: `${code} (${symbol})`,
      }));
    },

    languageOptions() {
      if (!this.languages) return [];
      return this.languages.map(({ id, title }) => ({
        id,
        text: title,
      }));
    },

    selectedCurrency: {
      get() {
        return this.currency?.code;
      },
      set(currencyCode) {
        this.changeCurrency(currencyCode);
      },
    },

    selectedLanguage: {
      get() {
        return this.currentLanguage;
      },
      set(langId) {
        this.changeLocale(langId);
      },
    },
  },
  methods: {
    openCartCheckout() {
      this.$emit("openCartCheckout");
      this.$refs.cartDropdown.hide();
    },
    sellSkin() {
      if (this.isAuth) {
        this.$emit("openSellSkinModal");
      } else {
        this.openSignIn();
      }
    },
    ...mapActions("modals", ["openCartDropdown", "closeCartDropdown"]),

    handleCloseCartDropdown() {
      this.closeCartDropdown();
      if (this.$refs.cartDropdown) {
        this.$refs.cartDropdown.hide();
      }
    },
    handleCartClick() {
      this.$emit("closeMenu");
      this.openCartDropdown();
    },
    handleCurrencyChange({ id }) {
      this.selectedCurrency = id;
    },

    handleLanguageChange({ id }) {
      this.selectedLanguage = id;
    },

    ...mapActions("app", ["changeLocale", "changeCurrency"]),

    openSignIn() {
      this.$emit("openSignIn");
    },

    openSignUp() {
      this.$emit("openSignUp");
    },
  },
};
</script>

<style lang="scss" scoped>
.menu {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #090000;
  z-index: 5;
  padding: 80px 32px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .select-container {
    width: max-content;
  }

  .link-container {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  .profile-button {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 46px;
    height: 46px;
    padding: 11px;
    margin-top: 24px;
  }
  /* Cart Button */
  .cart-button {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    background-color: transparent;
    width: 46px;
    height: 46px;
    padding: 11px;
    position: relative;
  }

  .cart-button img {
    width: 20px;
    height: 20px;
  }

  .cart-indicator {
    position: absolute;
    right: 6px;
    bottom: 6px;
    transform: translate(50%, 50%);
    width: 5px;
    height: 5px;
    background-color: #6ec226;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0;
    font-weight: 700;
    color: #151522;
  }
  .user-menu .icon {
    margin-top: 16px;
  }
  .icon {
    display: inline-flex;
    width: 44px;
    height: 44px;
    // margin-right: 16px;
    // margin-top: 16px;
    justify-content: center;
  }

  .nav__item {
    text-transform: uppercase;
    color: #fff;
    font-weight: 700;
    font-size: 16px;
  }

  .nav__item:not(:first-child) {
    margin-top: 24px;
  }

  .nav__item .nav-link {
    color: #fff;
    font-weight: 700;
    font-size: 16px;
  }
}

.icon {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
